import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import ListComponent from "./ListComponent";
import RadioComponent from "./RadioComponent";
import TableComponent from "./TableComponent";
import TextInputComponent from "./TextComponent";
import VideoComponent from "./VideoComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Section from "./Section";
import Span from "./Span";
import Division from "./Division";
import MainComponent from "./MainComponent";
import ArticleComponent from "./ArticleComponent";
import AsideComponent from "./AsideComponent";
import BRTag from "./BRTag";
import HRTag from "./HRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const FormComponent = ({ form = {} }) => {
  const { addDivision, hoveredDivision, addElement, addCanvasDrop, files } =
    useContext(CanvasContext);
  const formRef = useRef(null);

  const isHovered = form.id === hoveredDivision;
  const [isDivHovered, setIsDivHovered] = useState(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const [isDivActive, setIsDivActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (!monitor.didDrop()) {
          if (item.type === "DroppableDiv") {
            addDivision(form.id, item.name, item.type);
          } else if (
            item.type === "DroppableNav" ||
            item.type === "DroppableHeader" ||
            item.type === "DroppableHGroup" ||
            item.type === "DroppableFooter" ||
            item.type === "DroppableSection" ||
            item.type === "DroppableSpan" ||
            item.type === "DroppableForm" ||
            item.type === "DroppableMain" ||
            item.type === "DroppableArticle" ||
            item.type === "DroppableAside"
          ) {
            addCanvasDrop(form.id, item.name, item.type);
          } else {
            addElement(form.id, item.name, item.type);
          }
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = form;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = form;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = form;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = form;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const generateKeyframes = () => {
    if (!form.keyframes || form.keyframes.length === 0) return null;

    return form.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  const combinedRef = useMemo(
    () => (node) => {
      formRef.current = node;
      drop(node);
    },
    [drop]
  );
  useEffect(() => {
    if (formRef.current && form.keyframes && form.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = formRef.current.animate(keyframes, {
        duration: parseFloat(form.animationDuration) * 1000 || 1000,
        delay: parseFloat(form.animationDelay) * 1000 || 0,
        iterations:
          form.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(form.animationIterationCount) || 1,
        direction: form.animationDirection || "normal",
        fill: form.animationFillMode || "none",
        easing: form.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.keyframes,
    form.animationDuration,
    form.animationDelay,
    form.animationIterationCount,
    form.animationDirection,
    form.animationFillMode,
    form.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (form.backgroundImage && form.backgroundImage.startsWith("./")) {
        const imageName = form.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(form.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [form.backgroundImage, files]);

  const focusedStyles =
    isDivFocused && form.pseudoClass === "focus"
      ? {
          width: form.focusedwidth || form.width || "100%",
          height: form.focusedheight || form.height || "auto",

          padding: form.focusedpadding || form.padding || "8px",
          paddingTop: form.focusedpaddingTop || form.focusedpadding || "8px",
          paddingRight:
            form.focusedpaddingRight || form.focusedpadding || "8px",
          paddingBottom:
            form.focusedpaddingBottom || form.focusedpadding || "8px",
          paddingLeft: form.focusedpaddingLeft || form.focusedpadding || "8px",
          margin: form.focusedmargin || form.margin || "0px",
          marginTop: form.focusedmarginTop || form.focusedmargin || "0px",
          marginRight: form.focusedmarginRight || form.focusedmargin || "0px",
          marginBottom: form.focusedmarginBottom || form.focusedmargin || "0px",
          marginLeft: form.focusedmarginLeft || form.focusedmargin || "0px",

          borderTop:
            form.focusedborderTop ||
            form.focusedborder ||
            form.border ||
            "1px solid black",
          borderRight:
            form.focusedborderRight ||
            form.focusedborder ||
            form.border ||
            "1px solid black",
          borderBottom:
            form.focusedborderBottom ||
            form.focusedborder ||
            form.border ||
            "1px solid black",
          borderLeft:
            form.focusedborderLeft ||
            form.focusedborder ||
            form.border ||
            "1px solid black",
          borderSapcing:
            form.focusedborderSpacing || form.borderSpacing || "0px",
          borderCollapse:
            form.focusedborderCollapse || form.borderCollapse || "0px",
          borderRadius: form.focusedborderRadius || form.borderRadius || "0",
          outline: form.focusedoutline || form.outline || "none",

          position: form.focusedposition || form.position || "relative",
          top: form.focusedtop || form.top || "auto",
          right: form.focusedright || form.right || "auto",
          bottom: form.focusedbottom || form.bottom || "auto",
          left: form.focusedleft || form.left || "auto",
          zIndex: form.focusedzIndex || form.zIndex || "auto",
          boxShadow: form.focusedboxShadow || form.boxShadow || "none",
          opacity: form.focusedOpacity || form.opacity || 1,
          display: form.focuseddisplay || form.display || "block",
          flexDirection:
            form.focusedflexDirection || form.flexDirection || "column",
          flexWrap: form.focusedflexWrap || "nowrap",
          order: form.focusedorder || "0",
          flexGrow: form.focusedflexGrow || "0",
          flexShrink: form.focusedflexShrink || "1",
          flexBasis: form.focusedflexBasis || "auto",

          gridTemplateColumns: form.focusedgridTemplateColumns || "none",
          gridTemplateRows: form.focusedgridTemplateRows || "none",
          gridArea: form.focusedgridArea || "auto",
          gridAutoFlow: form.focusedgridAutoFlow || "row",
          gridAutoColumns: form.focusedgridAutoColumns || "auto",
          gridAutoRows: form.focusedgridAutoRows || "auto",
          columnGap: form.focusedcolumnGap || "0",
          rowGap: form.focusedrowGap || "0",

          justifyContent: form.focusedjustifyContent || "flex-start",
          alignItems: form.focusedalignItems || "stretch",
          alignContent: form.focusedalignContent || "stretch",
          alignSelf: form.focusedalignSelf || "auto",
          // placeContent: form.focusedplaceContent || "normal",
          // placeItems: form.focusedplaceItems || "normal",
          justifyItems: form.focusedjustifyItems || "stretch",
          justifySelf: form.focusedjustifySelf || "auto",

          gap: form.focusedgap || "0",
          visibility: form.focusedvisibility || "visible",

          color: form.focusedcolor || form.color || "#000000",
          backgroundColor:
            form.focusedbackgroundColor || form.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (form.focusedbackgroundImage) {
              if (form.focusedbackgroundImage.startsWith("./")) {
                const imageName = form.focusedbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.focusedbackgroundImage})`;
            }
            if (form.backgroundImage) {
              if (form.backgroundImage.startsWith("./")) {
                const imageName = form.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            form.focusedbackgroundPosition ||
            form.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            form.focusedbackgroundSize || form.backgroundSize || "auto",
          backgroundRepeat:
            form.focusedbackgroundRepeat || form.backgroundRepeat || "repeat",
          backgroundAttachment:
            form.focusedbackgroundAttachment ||
            form.backgroundAttachment ||
            "scroll",

          fontFamily: form.focusedfontFamily || form.fontFamily || "inherit",
          fontSize: form.focusedfontSize || form.fontSize || "inherit",
          fontWeight: form.focusedfontWeight || form.fontWeight || "normal",
          fontStyle: form.focusedfontStyle || form.fontStyle || "normal",
          fontVariant: form.focusedfontVariant || form.fontVariant || "normal",
          lineHeight: form.focusedlineHeight || form.lineHeight || "normal",
          textShadow: form.focusedtextShadow || form.textShadow || "none",
          textAlign: form.focusedtextAlign || form.textAlign || "left",
          verticalAlign:
            form.focusedverticalAlign || form.verticalAlign || "baseline",
          textDecoration:
            form.focusedtextDecoration || form.textDecoration || "none",
          textTransform:
            form.focusedtextTransform || form.textTransform || "none",
          letterSpacing:
            form.focusedletterSpacing || form.letterSpacing || "normal",
          wordSpacing: form.focusedwordSpacing || form.wordSpacing || "normal",
          textIndent: form.focusedtextIndent || form.textIndent || "0",
          whiteSpace: form.focusedwhiteSpace || form.whiteSpace || "normal",
          textOverflow: form.focusedtextOverflow || form.textOverflow || "clip",
          direction: form.focuseddirection || form.direction || "ltr",
          unicodeBidi: form.focusedunicodeBidi || form.unicodeBidi || "normal",
          writingMode:
            form.focusedwritingMode || form.writingMode || "horizontal-tb",

          cursor: form.focusedcursor || form.cursor || "default",
          filter: form.focusedfilter || form.filter || "none",
          backdropFilter:
            form.focusedbackdropFilter || form.backdropFilter || "none",
          mixBlendMode:
            form.focusedmixBlendMode || form.mixBlendMode || "normal",
          aspectRatio: form.focusedaspectRatio || form.aspectRatio || "auto",

          transform: form.focusedtransform || form.transform || "none",
          transition: form.focusedtransition || form.transition || "none",
        }
      : {};

  const hoverStyles =
    isDivHovered && form.pseudoClass === "hover"
      ? {
          width: form.hoveredwidth || form.width || "100%",
          height: form.hoveredheight || form.height || "auto",

          padding: form.hoveredpadding || form.padding || "8px",
          paddingTop: form.hoveredpaddingTop || form.hoveredpadding || "8px",
          paddingRight:
            form.hoveredpaddingRight || form.hoveredpadding || "8px",
          paddingBottom:
            form.hoveredpaddingBottom || form.hoveredpadding || "8px",
          paddingLeft: form.hoveredpaddingLeft || form.hoveredpadding || "8px",
          margin: form.hoveredmargin || form.margin || "0px",
          marginTop: form.hoveredmarginTop || form.hoveredmargin || "0px",
          marginRight: form.hoveredmarginRight || form.hoveredmargin || "0px",
          marginBottom: form.hoveredmarginBottom || form.hoveredmargin || "0px",
          marginLeft: form.hoveredmarginLeft || form.hoveredmargin || "0px",

          borderTop:
            form.hoveredborderTop ||
            form.hoveredborder ||
            form.border ||
            "1px solid black",
          borderRight:
            form.hoveredborderRight ||
            form.hoveredborder ||
            form.border ||
            "1px solid black",
          borderBottom:
            form.hoveredborderBottom ||
            form.hoveredborder ||
            form.border ||
            "1px solid black",
          borderLeft:
            form.hoveredborderLeft ||
            form.hoveredborder ||
            form.border ||
            "1px solid black",
          borderSapcing:
            form.hoveredborderSpacing || form.borderSpacing || "0px",
          borderCollapse:
            form.hoveredborderCollapse || form.borderCollapse || "0px",
          borderRadius: form.hoveredborderRadius || form.borderRadius || "0",
          outline: form.hoveredoutline || form.outline || "none",

          position: form.hoveredposition || form.position || "relative",
          top: form.hoveredtop || form.top || "auto",
          right: form.hoveredright || form.right || "auto",
          bottom: form.hoveredbottom || form.bottom || "auto",
          left: form.hoveredleft || form.left || "auto",
          zIndex: form.hoveredzIndex || form.zIndex || "auto",
          boxShadow: form.hoveredboxShadow || form.boxShadow || "none",
          opacity: form.hoveredOpacity || form.opacity || 1,
          display: form.hovereddisplay || form.display || "block",
          flexDirection:
            form.hoveredflexDirection || form.flexDirection || "column",
          flexWrap: form.hoveredflexWrap || "nowrap",
          order: form.hoveredorder || "0",
          flexGrow: form.hoveredflexGrow || "0",
          flexShrink: form.hoveredflexShrink || "1",
          flexBasis: form.hoveredflexBasis || "auto",

          gridTemplateColumns: form.hoveredgridTemplateColumns || "none",
          gridTemplateRows: form.hoveredgridTemplateRows || "none",
          gridArea: form.hoveredgridArea || "auto",
          gridAutoFlow: form.hoveredgridAutoFlow || "row",
          gridAutoColumns: form.hoveredgridAutoColumns || "auto",
          gridAutoRows: form.hoveredgridAutoRows || "auto",
          columnGap: form.hoveredcolumnGap || "0",
          rowGap: form.hoveredrowGap || "0",

          justifyContent: form.hoveredjustifyContent || "flex-start",
          alignItems: form.hoveredalignItems || "stretch",
          alignContent: form.hoveredalignContent || "stretch",
          alignSelf: form.hoveredalignSelf || "auto",
          // placeContent: form.hoveredplaceContent || "normal",
          // placeItems: form.hoveredplaceItems || "normal",
          justifyItems: form.hoveredjustifyItems || "stretch",
          justifySelf: form.hoveredjustifySelf || "auto",

          gap: form.hoveredgap || "0",
          visibility: form.hoveredvisibility || "visible",

          color: form.hoveredcolor || form.color || "#000000",
          backgroundColor:
            form.hoveredbackgroundColor || form.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (form.hoveredbackgroundImage) {
              if (form.hoveredbackgroundImage.startsWith("./")) {
                const imageName = form.hoveredbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.hoveredbackgroundImage})`;
            }
            if (form.backgroundImage) {
              if (form.backgroundImage.startsWith("./")) {
                const imageName = form.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            form.hoveredbackgroundPosition ||
            form.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            form.hoveredbackgroundSize || form.backgroundSize || "auto",
          backgroundRepeat:
            form.hoveredbackgroundRepeat || form.backgroundRepeat || "repeat",
          backgroundAttachment:
            form.hoveredbackgroundAttachment ||
            form.backgroundAttachment ||
            "scroll",

          fontFamily: form.hoveredfontFamily || form.fontFamily || "inherit",
          fontSize: form.hoveredfontSize || form.fontSize || "inherit",
          fontWeight: form.hoveredfontWeight || form.fontWeight || "normal",
          fontStyle: form.hoveredfontStyle || form.fontStyle || "normal",
          fontVariant: form.hoveredfontVariant || form.fontVariant || "normal",
          lineHeight: form.hoveredlineHeight || form.lineHeight || "normal",
          textShadow: form.hoveredtextShadow || form.textShadow || "none",
          textAlign: form.hoveredtextAlign || form.textAlign || "left",
          verticalAlign:
            form.hoveredverticalAlign || form.verticalAlign || "baseline",
          textDecoration:
            form.hoveredtextDecoration || form.textDecoration || "none",
          textTransform:
            form.hoveredtextTransform || form.textTransform || "none",
          letterSpacing:
            form.hoveredletterSpacing || form.letterSpacing || "normal",
          wordSpacing: form.hoveredwordSpacing || form.wordSpacing || "normal",
          textIndent: form.hoveredtextIndent || form.textIndent || "0",
          whiteSpace: form.hoveredwhiteSpace || form.whiteSpace || "normal",
          textOverflow: form.hoveredtextOverflow || form.textOverflow || "clip",
          direction: form.hovereddirection || form.direction || "ltr",
          unicodeBidi: form.hoveredunicodeBidi || form.unicodeBidi || "normal",
          writingMode:
            form.hoveredwritingMode || form.writingMode || "horizontal-tb",

          cursor: form.hoveredcursor || form.cursor || "default",
          filter: form.hoveredfilter || form.filter || "none",
          backdropFilter:
            form.hoveredbackdropFilter || form.backdropFilter || "none",
          mixBlendMode:
            form.hoveredmixBlendMode || form.mixBlendMode || "normal",
          aspectRatio: form.hoveredaspectRatio || form.aspectRatio || "auto",

          transform: form.hoveredtransform || form.transform || "none",
          transition: form.hoveredtransition || form.transition || "none",
        }
      : {};

  const activeStyles =
    isDivActive && form.pseudoClass === "active"
      ? {
          width: form.activatedwidth || form.width || "100%",
          height: form.hoveredheight || form.height || "auto",

          padding: form.activatedpadding || form.padding || "8px",
          paddingTop:
            form.activatedpaddingTop || form.activatedpadding || "8px",
          paddingRight:
            form.activatedpaddingRight || form.activatedpadding || "8px",
          paddingBottom:
            form.activatedpaddingBottom || form.activatedpadding || "8px",
          paddingLeft:
            form.activatedpaddingLeft || form.activatedpadding || "8px",
          margin: form.activatedmargin || form.margin || "0px",
          marginTop: form.activatedmarginTop || form.activatedmargin || "0px",
          marginRight:
            form.activatedmarginRight || form.activatedmargin || "0px",
          marginBottom:
            form.activatedmarginBottom || form.activatedmargin || "0px",
          marginLeft: form.activatedmarginLeft || form.activatedmargin || "0px",

          borderTop:
            form.activatedborderTop ||
            form.activatedborder ||
            form.border ||
            "1px solid black",
          borderRight:
            form.activatedborderRight ||
            form.activatedborder ||
            form.border ||
            "1px solid black",
          borderBottom:
            form.activatedborderBottom ||
            form.activatedborder ||
            form.border ||
            "1px solid black",
          borderLeft:
            form.activatedborderLeft ||
            form.activatedborder ||
            form.border ||
            "1px solid black",
          borderSapcing:
            form.activatedborderSpacing || form.borderSpacing || "0px",
          borderCollapse:
            form.activatedborderCollapse || form.borderCollapse || "0px",
          borderRadius: form.activatedborderRadius || form.borderRadius || "0",
          outline: form.activatedoutline || form.outline || "none",

          position: form.activatedposition || form.position || "relative",
          top: form.activatedtop || form.top || "auto",
          right: form.activatedright || form.right || "auto",
          bottom: form.activatedbottom || form.bottom || "auto",
          left: form.activatedleft || form.left || "auto",
          zIndex: form.activatedzIndex || form.zIndex || "auto",

          boxShadow: form.activatedboxShadow || form.boxShadow || "none",
          opacity: form.activatedOpacity || form.opacity || 1,
          display: form.activateddisplay || form.display || "block",
          flexDirection:
            form.activatedflexDirection || form.flexDirection || "column",
          flexWrap: form.activatedflexWrap || "nowrap",
          order: form.activatedorder || "0",
          flexGrow: form.activatedflexGrow || "0",
          flexShrink: form.activatedflexShrink || "1",
          flexBasis: form.activatedflexBasis || "auto",

          gridTemplateColumns: form.activatedgridTemplateColumns || "none",
          gridTemplateRows: form.activatedgridTemplateRows || "none",
          gridArea: form.activatedgridArea || "auto",
          gridAutoFlow: form.activatedgridAutoFlow || "row",
          gridAutoColumns: form.activatedgridAutoColumns || "auto",
          gridAutoRows: form.activatedgridAutoRows || "auto",
          columnGap: form.activatedcolumnGap || "0",
          rowGap: form.activatedrowGap || "0",

          justifyContent: form.activatedjustifyContent || "flex-start",
          alignItems: form.activatedalignItems || "stretch",
          alignContent: form.activatedalignContent || "stretch",
          alignSelf: form.activatedalignSelf || "auto",
          // placeContent: form.activatedplaceContent || "normal",
          // placeItems: form.activatedplaceItems || "normal",
          justifyItems: form.activatedjustifyItems || "stretch",
          justifySelf: form.activatedjustifySelf || "auto",

          gap: form.activatedgap || "0",
          visibility: form.activatedvisibility || "visible",

          color: form.activatedcolor || form.color || "#000000",
          backgroundColor:
            form.activatedbackgroundColor || form.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (form.activatedbackgroundImage) {
              if (form.activatedbackgroundImage.startsWith("./")) {
                const imageName = form.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.activatedbackgroundImage})`;
            }
            if (form.backgroundImage) {
              if (form.backgroundImage.startsWith("./")) {
                const imageName = form.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${form.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            form.activatedbackgroundPosition ||
            form.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            form.activatedbackgroundSize || form.backgroundSize || "auto",
          backgroundRepeat:
            form.activatedbackgroundRepeat || form.backgroundRepeat || "repeat",
          backgroundAttachment:
            form.activatedbackgroundAttachment ||
            form.backgroundAttachment ||
            "scroll",

          fontFamily: form.activatedfontFamily || form.fontFamily || "inherit",
          fontSize: form.activatedfontSize || form.fontSize || "inherit",
          fontWeight: form.activatedfontWeight || form.fontWeight || "normal",
          fontStyle: form.activatedfontStyle || form.fontStyle || "normal",
          fontVariant:
            form.activatedfontVariant || form.fontVariant || "normal",
          lineHeight: form.activatedlineHeight || form.lineHeight || "normal",
          textShadow: form.activatedtextShadow || form.textShadow || "none",
          textAlign: form.activatedtextAlign || form.textAlign || "left",
          verticalAlign:
            form.activatedverticalAlign || form.verticalAlign || "baseline",
          textDecoration:
            form.activatedtextDecoration || form.textDecoration || "none",
          textTransform:
            form.activatedtextTransform || form.textTransform || "none",
          letterSpacing:
            form.activatedletterSpacing || form.letterSpacing || "normal",
          wordSpacing:
            form.activatedwordSpacing || form.wordSpacing || "normal",
          textIndent: form.activatedtextIndent || form.textIndent || "0",
          whiteSpace: form.activatedwhiteSpace || form.whiteSpace || "normal",
          textOverflow:
            form.activatedtextOverflow || form.textOverflow || "clip",
          direction: form.activateddirection || form.direction || "ltr",
          unicodeBidi:
            form.activatedunicodeBidi || form.unicodeBidi || "normal",
          writingMode:
            form.activatedwritingMode || form.writingMode || "horizontal-tb",

          cursor: form.activatedcursor || form.cursor || "default",
          filter: form.activatedfilter || form.filter || "none",
          backdropFilter:
            form.activatedbackdropFilter || form.backdropFilter || "none",
          mixBlendMode:
            form.activatedmixBlendMode || form.mixBlendMode || "normal",
          aspectRatio: form.activatedaspectRatio || form.aspectRatio || "auto",

          transform: form.activatedtransform || form.transform || "none",
          transition: form.activatedtransition || form.transition || "none",
        }
      : {};

  const FormStyles = {
    width: form.width || "100%",
    height: form.height || "auto",
    padding: form.padding || "8px",
    paddingTop: form.paddingTop || form.padding || "8px",
    paddingRight: form.paddingRight || form.padding || "8px",
    paddingBottom: form.paddingBottom || form.padding || "8px",
    paddingLeft: form.paddingLeft || form.padding || "8px",
    margin: form.margin || "0px",
    marginTop: form.marginTop || form.margin || "0px",
    marginRight: form.marginRight || form.margin || "0px",
    marginBottom: form.marginBottom || form.margin || "0px",
    marginLeft: form.marginLeft || form.margin || "0px",

    borderTop: form.borderTop || form.border || "1px solid #000000",
    borderRight: form.borderRight || form.border || "1px solid #000000",
    borderBottom: form.borderBottom || form.border || "1px solid #000000",
    borderLeft: form.borderLeft || form.border || "1px solid #000000",

    borderSapcing: form.borderSpacing || "0px",
    borderCollapse: form.borderCollapse || "0px",
    borderRadius: form.borderRadius || "0",
    boxSizing: form.boxSizing || "border-box",

    position: form.position || "relative",
    top: form.top || "auto",
    right: form.right || "auto",
    bottom: form.bottom || "auto",
    left: form.left || "auto",
    zIndex: form.zIndex || "auto",

    display: form.display || "block",
    flexDirection: form.flexDirection || "column",
    flexWrap: form.flexWrap || "nowrap",
    order: form.order || "0",
    flexGrow: form.flexGrow || "0",
    flexShrink: form.flexShrink || "1",
    flexBasis: form.flexBasis || "auto",

    gridTemplateColumns: form.gridTemplateColumns || "none",
    gridTemplateRows: form.gridTemplateRows || "none",
    gridArea: form.gridArea || "auto",
    gridAutoFlow: form.gridAutoFlow || "row",
    gridAutoColumns: form.gridAutoColumns || "auto",
    gridAutoRows: form.gridAutoRows || "auto",
    columnGap: form.columnGap || "0",
    rowGap: form.rowGap || "0",

    justifyContent: form.justifyContent || "flex-start",
    alignItems: form.alignItems || "stretch",
    alignContent: form.alignContent || "stretch",
    alignSelf: form.alignSelf || "auto",
    // placeContent: form.placeContent || "normal",
    // placeItems: form.placeItems || "normal",
    justifyItems: form.justifyItems || "stretch",
    justifySelf: form.justifySelf || "auto",

    gap: form.gap || "0",
    visibility: form.visibility || "visible",

    color: form.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: form.backgroundPosition || "0% 0%",
    backgroundSize: form.backgroundSize || "auto",
    backgroundRepeat: form.backgroundRepeat || "repeat",
    backgroundAttachment: form.backgroundAttachment || "scroll",
    boxShadow: form.boxShadow || "none",
    filter: form.filter || "none",
    backdropFilter: form.backdropFilter || "none",
    mixBlendMode: form.mixBlendMode || "normal",
    aspectRatio: form.aspectRatio || "auto",

    resize: form.resize || "none",
    userSelect: form.userSelect || "auto",
    overflow: form.resize && form.resize !== "none" ? "auto" : "visible",
    overflowX: form.overflowX || "hidden",
    overflowY: form.overflowY || "hidden",

    fontSize: form.fontSize || "inherit",
    fontFamily: form.fontFamily || "inherit",
    fontWeight: form.fontWeight || "normal",
    fontStyle: form.fontStyle || "normal",
    fontVariant: form.fontVariant || "normal",
    lineHeight: form.lineHeight || "normal",
    textAlign: form.textAlign || "left",
    textDecoration: form.textDecoration || "none",
    textTransform: form.textTransform || "none",
    textShadow: form.textShadow || "none",
    verticalAlign: form.verticalAlign || "baseline",
    letterSpacing: form.letterSpacing || "normal",
    wordSpacing: form.wordSpacing || "normal",
    textIndent: form.textIndent || "0",
    whiteSpace: form.whiteSpace || "normal",
    textOverflow: form.textOverflow || "clip",
    direction: form.direction || "ltr",
    unicodeBidi: form.unicodeBidi || "normal",
    writingMode: form.writingMode || "horizontal-tb",
    cursor: form.cursor || "default",
    outline: form.outline || "none",

    backgroundColor: isHovered ? "#ADD8E6" : form.backgroundColor || "#ffffff",

    listStyleType: form.listStyleType || "disc",
    listStyleImage: form.listStyleImage
      ? `url(${form.listStyleImage})`
      : "none",
    listStylePosition: form.listStylePosition || "outside",

    transform: form.transform || "none",
    transition:
      form.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <form
      className="Main_Division"
      ref={combinedRef}
      name={form.Formname || ""}
      action={form.action || "/submit"}
      method={form.method || "post"}
      encType={form.enctype || "multipart/form-data"}
      target={form.target || "_blank"}
      autoComplete={form.autocomplete === "true" ? "on" : "off"}
      noValidate={form.novalidate === "true"}
      style={FormStyles}
      onMouseEnter={() => setIsDivHovered(true)}
      onMouseLeave={() => setIsDivHovered(false)}
      onFocus={() => setIsDivFocused(true)}
      onBlur={() => setIsDivFocused(false)}
      onMouseDown={() => setIsDivActive(true)}
      onMouseUp={() => setIsDivActive(false)}
      tabIndex={0}
    >
      {form.children &&
        form.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </form>
  );
};

export default FormComponent;
